import { GapDialogProps } from './GapDialog'
import { createStoreContext } from 'reusable/mobx-store-utils/storeUtils'
import React from 'react'
import Typography from '@mui/material/Typography'
import { notNull } from '@gap/utils/utils'
import { themeConfig, DialogSize } from '@gap/shared/theme'
import { GapDialogAction } from './GapDialogActionButton'
import { DialogProps } from '@mui/material/Dialog'
import { SxProps, Theme } from '@mui/material/styles'
import { makeAutoObservable, observable } from 'mobx'

interface GapDialogStoreProps extends GapDialogProps { }

export interface GapDialogChildOptions {
   title: string
   subTitle?: string
   primaryButton?: GapDialogAction
}

/**
 * Normalizes the various accepted inputs for `setChildContent` into a `ReactNode`.
 *
 * Accepts:
 * - `null` / `undefined` / `boolean` → nothing
 * - `string` / `number` → wrapped in `<Typography>`
 * - React element (`<Foo />`) → used as-is
 * - Component type (function, class, `observer()`/`memo()`, `forwardRef`) → instantiated
 *
 * `ComponentType<any>` is used intentionally: callers may pass components with
 * any prop signature, and the store never forwards props (it instantiates with
 * `<ContentComponent />`), so the prop type is irrelevant at this boundary.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type AnyComponentType = React.ComponentType<any>

function normalizeChildContent(content: React.ReactNode | AnyComponentType): React.ReactNode {
   if (content == null || typeof content === 'boolean') return null
   if (typeof content === 'string' || typeof content === 'number') {
      return <Typography>{content}</Typography>
   }
   if (React.isValidElement(content)) return content
   if (typeof content === 'function' || (typeof content === 'object' && content !== null && '$$typeof' in content)) {
      const ContentComponent = content as AnyComponentType
      return <ContentComponent />
   }
   return content as React.ReactNode
}

export class GapDialogStore {
   sp
   isOpened = true
   childContent: React.ReactNode = null
   childOptions: GapDialogChildOptions | undefined
   slide: 'left' | 'right' = 'right'
   sizeOverride: DialogSize | undefined
   /** Used to override the default buttons for the dialog. This will take precidence over default buttons and buttons defined in the normal tabs */
   overrideButtons: {
      primary?: GapDialogAction
      secondary?: GapDialogAction
      cancel?: GapDialogAction | boolean
   } | undefined
   private selectedTabIndex: number | undefined = undefined

   constructor(sp: GapDialogStoreProps) {
      this.sp = sp
      // childContent stores a React element; React 19 attaches internal `_debugTask` etc to
      // elements, and mobx must not recursively make those internals observable (it would
      // proxy `_debugTask.run` through executeAction → "illegal receiver" at commit time).
      makeAutoObservable(this, { childContent: observable.ref })
   }

   /** The user's pick while the dialog is open, else the caller's `initialTabIndex`, else the first tab. */
   get tabIndex(): number {
      return this.selectedTabIndex ?? this.sp.initialTabIndex ?? 0
   }

   // Get the main content. This would be the children, inless tabs are defined, then return based on tab
   get mainContent() {
      const mainContent = this.sp.tabs ? notNull(this.sp.tabs[this.tabIndex]).component : this.sp.children
      if (typeof mainContent === 'string') {
         return <Typography variant="body1" color={themeConfig.primary[600]}>
            {mainContent}
         </Typography>
      }
      return mainContent
   }

   get size() {
      return this.sizeOverride || this.sp.tabs?.[this.tabIndex]?.size || this.sp.size
   }

   get dialogProps(): Omit<DialogProps, 'open'> {
      const dialogProps = this.sp.DialogProps || {}
      dialogProps.sx ||= {}
      dialogProps.sx = {
         height: '100%',
         minWidth: '200px',
         ...dialogProps.sx,
      }
      if (!dialogProps.maxWidth && !this.size) {
         dialogProps.maxWidth = 'xs'
      }

      dialogProps.slotProps ??= {}
      dialogProps.slotProps.paper = (dialogProps.slotProps.paper || {}) as { sx?: SxProps<Theme> }
      dialogProps.slotProps.paper.sx = {
         minWidth: '220px',
         transition: 'width 250ms ease, max-width 250ms ease',
         ...(this.size ? {
            width: themeConfig.dialogSizes[this.size],
            maxWidth: themeConfig.dialogSizes[this.size]
         } : {}),
         ...dialogProps.slotProps.paper.sx,
      }

      return dialogProps
   }

   get parentDefinesOpen() {
      return typeof this.sp.open === 'boolean'
   }

   get open() {
      if (this.sp.modalState) {
         return this.sp.modalState.visible
      }
      if (this.parentDefinesOpen) {
         return !!this.sp.open
      } else {
         return this.isOpened
      }
   }

   get showClose() {
      return !!this.sp.modalState
   }

   get showDivider() {
      return this.sp.showDivider
   }

   get cancelOption() {
      if (typeof this.buttons.cancel === 'boolean') {
         if (this.buttons.cancel) {
            return {
               name: 'Cancel'
            }
         }
      } else if (!!this.buttons.cancel && this.buttons.cancel.name) {
         return this.buttons.cancel
      }
      return null
   }

   get dialogHasActions() {
      return this.cancelOption || this.sp.primary || this.sp.secondary || this.overrideButtons
   }

   get title() {
      return this.childOptions?.title || this.sp.title
   }

   get subTitle() {
      return this.childOptions?.subTitle || this.sp.subTitle
   }

   get buttons() {
      let primary = this.sp.primary
      let secondary = this.sp.secondary
      let cancel = this.sp.cancel

      if (this.sp.tabs) {
         const buttons = this.sp.tabs[this.tabIndex]?.buttons

         if (buttons) {
            primary = buttons.primary
            secondary = buttons.secondary
            cancel = buttons.cancel
         }
      }

      if (this.overrideButtons) {
         primary = this.overrideButtons.primary
         secondary = this.overrideButtons.secondary
         cancel = this.overrideButtons.cancel
      }

      return { primary, secondary, cancel }
   }

   setSize = (size: DialogSize) => {
      this.sizeOverride = size
   }

   changeTabs = (_: React.SyntheticEvent, newTabIndex: number) => {
      this.selectedTabIndex = newTabIndex
   }

   setChildContent = (content: React.ReactNode | AnyComponentType, options?: GapDialogChildOptions) => {
      this.slide = content ? 'left' : 'right'

      setTimeout(() => {
         this.childContent = normalizeChildContent(content)
         this.childOptions = options
      }, 50)
   }

   resetChildContent = () => {
      this.setChildContent(null)
   }

   handleDirectClose = () => {
      if (this.sp.clickAwayAction) {
         return this.handleClose(this.sp.clickAwayAction)
      }
      return this.handleClose()
   }

   handleClose = async (action?: GapDialogAction) => {
      // in case the caller is using async promises
      if (!!this.sp.resolve) {
         if (!!action) {
            this.sp.resolve(action)
         } else {
            this.sp.resolve({ name: '' })
         }
      }

      /** determines if the action will close the dialog automatically */
      const closeAutom = !action?.notCloseAutomatically

      if (!!action) {
         if (!!action.callback) {
            await action.callback({
               closeDialog: closeAutom ? () => null : this.closeDialog
            })
         }
      }

      if (action?.notCloseAutomatically) {
         return
      }
      if (closeAutom) {
         this.closeDialog()
      }

      if (this.sp.modalState) {
         this.sp.modalState.close()
         this.closeDialog()
      }
   }

   closeDialog = () => {
      // Reset child content & tabs
      this.childContent = null
      this.selectedTabIndex = undefined
      this.isOpened = false
   }
}

export const { Provider: GapDialogStoreProvider, useProvidedStore: useGapDialogStore } = createStoreContext(GapDialogStore)
